import { useEffect, useRef, useState } from 'react'

type Fish = {
  x: number
  y: number
  vx: number
  vy: number
  size: number
  alpha: number
  phase: number
  /** 1 = alive, 0–1 = dissolving, -1 = waiting to respawn */
  life: number
  respawnIn: number
  /** Evasive juke: side preference + countdown */
  jukeDir: number
  jukeIn: number
}

type Particle = {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  size: number
}

const PREDATOR_SIZE = 68
/** Bite reach as a fraction of current drawn size (from the mouth, not body center) */
const BITE_SIZE_FRAC = 0.32
const BITE_SIZE_FRAC_HUNT = 0.38
/** Mouth sits this far forward along heading, as a fraction of drawn size */
const MOUTH_FORWARD_FRAC = 0.22
const EAT_DURATION = 0.22
/** Mouth opens when prey is this close */
const OPEN_MOUTH_DIST = 150
/** Hold open mouth briefly after a catch, then return to closed swim */
const OPEN_HOLD = 0.22
/** How far past the hero edges the Gigafish may travel */
const PREDATOR_OVERFLOW = 0.1
/** Compound size growth per fish eaten — capped at sprite bake resolution */
const GROW_PER_EAT = 1.01

/** Profile + 8 depth angles (1–4 toward camera, 5–8 away) */
const SWIM_SPRITES = [
  '/assets/gigafish-sprite-swim.svg',
  '/assets/gigafish-sprite-swim1.svg',
  '/assets/gigafish-sprite-swim2.svg',
  '/assets/gigafish-sprite-swim3.svg',
  '/assets/gigafish-sprite-swim4.svg',
  '/assets/gigafish-sprite-swim5.svg',
  '/assets/gigafish-sprite-swim6.svg',
  '/assets/gigafish-sprite-swim7.svg',
  '/assets/gigafish-sprite-swim8.svg',
] as const
const OPEN_SPRITES = [
  '/assets/gigafish-sprite-open.svg',
  '/assets/gigafish-sprite-open1.svg',
  '/assets/gigafish-sprite-open2.svg',
  '/assets/gigafish-sprite-open3.svg',
  '/assets/gigafish-sprite-open4.svg',
  '/assets/gigafish-sprite-open5.svg',
  '/assets/gigafish-sprite-open6.svg',
  '/assets/gigafish-sprite-open7.svg',
  '/assets/gigafish-sprite-open8.svg',
] as const

const SPRITE_TINT = 'rgba(15,23,42,0.94)'
const SPRITE_TINT_SIZE = 256
/** Drawn size cap — below sprite bake so growth stays sharp */
const PREDATOR_SIZE_MAX = 160

/** Prey speeds in px/s */
const PREY_CRUISE = 68
const PREY_FLEE_SPEED = 105
const PREY_MAX_SPEED = 125

/** Boids perception (px) */
const SEPARATION_RADIUS = 28
const ALIGN_RADIUS = 92
const COHESION_RADIUS = 140
const FLEE_RADIUS = 175

/** Soft force weights */
const W_SEPARATION = 1.35
const W_ALIGNMENT = 1.05
const W_COHESION = 0.95
const W_FLEE = 2.35
const W_TEXT_ZONE = 0.55
const W_WANDER = 0.14

/** Soft ellipse over hero copy — bias, not a hard wall */
const TEXT_ZONE_RX = 0.24
const TEXT_ZONE_RY = 0.22
const TEXT_ZONE_CY = 0.42

function drawLittleFish(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  angle: number,
  size: number,
  alpha: number,
  scale = 1,
  color = '#0f172a',
) {
  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(angle)
  ctx.scale(scale, scale)
  ctx.globalAlpha = alpha
  ctx.fillStyle = color

  // Body
  ctx.beginPath()
  ctx.ellipse(0, 0, size * 1.1, size * 0.45, 0, 0, Math.PI * 2)
  ctx.fill()

  // Tail
  ctx.beginPath()
  ctx.moveTo(-size * 0.85, 0)
  ctx.lineTo(-size * 1.55, -size * 0.55)
  ctx.lineTo(-size * 1.35, 0)
  ctx.lineTo(-size * 1.55, size * 0.55)
  ctx.closePath()
  ctx.fill()

  ctx.restore()
}

/** Prefer margin bands so the school starts away from copy */
function marginSchoolAnchor(width: number, height: number): { x: number; y: number; heading: number } {
  const band = Math.floor(Math.random() * 4)
  const inset = 0.12 + Math.random() * 0.1
  if (band === 0) {
    return { x: width * (0.15 + Math.random() * 0.7), y: height * inset, heading: Math.PI * (0.15 + Math.random() * 0.7) }
  }
  if (band === 1) {
    return { x: width * (1 - inset), y: height * (0.2 + Math.random() * 0.6), heading: Math.PI * (0.6 + Math.random() * 0.8) }
  }
  if (band === 2) {
    return { x: width * (0.15 + Math.random() * 0.7), y: height * (1 - inset), heading: -Math.PI * (0.15 + Math.random() * 0.7) }
  }
  return { x: width * inset, y: height * (0.2 + Math.random() * 0.6), heading: Math.PI * (-0.3 + Math.random() * 0.6) }
}

function spawnAtEdge(width: number, height: number): Pick<Fish, 'x' | 'y' | 'vx' | 'vy'> {
  const edge = Math.floor(Math.random() * 4)
  const speed = PREY_CRUISE * (0.75 + Math.random() * 0.4)
  let x = 0
  let y = 0
  if (edge === 0) {
    x = Math.random() * width
    y = -24
  } else if (edge === 1) {
    x = width + 24
    y = Math.random() * height
  } else if (edge === 2) {
    x = Math.random() * width
    y = height + 24
  } else {
    x = -24
    y = Math.random() * height
  }
  // Enter along a margin lane, not straight through the copy
  const anchor = marginSchoolAnchor(width, height)
  const dir = Math.atan2(anchor.y - y, anchor.x - x)
  return {
    x,
    y,
    vx: Math.cos(dir) * speed,
    vy: Math.sin(dir) * speed,
  }
}

function createFish(
  width: number,
  height: number,
  fromEdge: boolean,
  cluster?: { x: number; y: number; heading: number },
): Fish {
  let spawn: Pick<Fish, 'x' | 'y' | 'vx' | 'vy'>
  if (fromEdge) {
    spawn = spawnAtEdge(width, height)
  } else if (cluster) {
    const spread = 36 + Math.random() * 50
    const jitter = (Math.random() - 0.5) * 0.7
    const heading = cluster.heading + jitter
    const speed = PREY_CRUISE * (0.85 + Math.random() * 0.3)
    spawn = {
      x: cluster.x + (Math.random() - 0.5) * spread,
      y: cluster.y + (Math.random() - 0.5) * spread,
      vx: Math.cos(heading) * speed,
      vy: Math.sin(heading) * speed,
    }
  } else {
    const anchor = marginSchoolAnchor(width, height)
    const speed = PREY_CRUISE * (0.85 + Math.random() * 0.3)
    spawn = {
      x: anchor.x,
      y: anchor.y,
      vx: Math.cos(anchor.heading) * speed,
      vy: Math.sin(anchor.heading) * speed,
    }
  }
  return {
    ...spawn,
    size: 5 + Math.random() * 5,
    alpha: 0.38 + Math.random() * 0.22,
    phase: Math.random() * Math.PI * 2,
    life: 1,
    respawnIn: 0,
    jukeDir: Math.random() < 0.5 ? -1 : 1,
    jukeIn: 0.2 + Math.random() * 0.5,
  }
}

function createSchool(width: number, height: number, count: number): Fish[] {
  // Spread into clusters so denser schools still read as packs, not one blob
  const groups = Math.min(16, Math.max(1, Math.ceil(count / 25)))
  const perGroup = Math.ceil(count / groups)
  const out: Fish[] = []
  for (let g = 0; g < groups; g++) {
    const cluster = marginSchoolAnchor(width, height)
    const n = Math.min(perGroup, count - out.length)
    for (let i = 0; i < n; i++) {
      out.push(createFish(width, height, false, cluster))
    }
  }
  return out
}

type HeroFishSchoolProps = {
  /** When false, only the little-fish school runs (no Gigafish). Default true. */
  showPredator?: boolean
  /** Soft bias / fade around centered copy. Default true (hero). */
  avoidCopy?: boolean
  /** Pin to the viewport so tall scroll pages don't stretch the canvas. */
  fixedToViewport?: boolean
  /** Fill color for little fish (opacity still comes from each fish’s alpha). */
  preyColor?: string
}

/** Autonomous hunt modes (coarse pointer / cursor left hero) */
type PredatorMode = 'patrol' | 'stalk' | 'herd' | 'strike'

type PackInfo = {
  cx: number
  cy: number
  vx: number
  vy: number
  count: number
  /** Living fish indices in the pack */
  members: number[]
}

const PACK_RADIUS = 95
const PACK_MIN = 3
/** Stalk ring — hang outside the pack before committing */
const STALK_DIST_MIN = 160
const STALK_DIST_MAX = 280
/** Give up a strike if it drags on */
const STRIKE_MAX = 2.8
/** How far a committed pack may drift before we treat it as gone */
const PACK_FOCUS_RADIUS = 160
/** Idle cursor over hero → resume autonomous hunt */
const CURSOR_IDLE_MS = 2500

/**
 * Ambient school of little fish.
 * Desktop (fine pointer): Gigafish follows the cursor and eats intercepts.
 * Mobile / coarse pointer: Gigafish patrols, stalks packs, herds, then strikes.
 * Pass showPredator={false} for a calm school-only background (e.g. project form).
 * Prefers-reduced-motion: nothing is mounted (a frozen school reads as broken).
 */
export default function HeroFishSchool({
  showPredator = true,
  avoidCopy = true,
  fixedToViewport = false,
  preyColor = '#0f172a',
}: HeroFishSchoolProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const [reducedMotion, setReducedMotion] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onChange = () => setReducedMotion(mq.matches)
    mq.addEventListener('change', onChange)
    onChange()
    return () => mq.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    if (reducedMotion) return

    const canvas = canvasRef.current
    const wrap = wrapRef.current
    if (!canvas || !wrap) return

    const ctx = canvas.getContext('2d', { alpha: true })
    if (!ctx) return

    const reducedMq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const fineMq = window.matchMedia('(pointer: fine)')
    let reduced = reducedMq.matches
    let finePointer = fineMq.matches
    const onMq = () => {
      reduced = reducedMq.matches
      finePointer = fineMq.matches
    }
    reducedMq.addEventListener('change', onMq)
    fineMq.addEventListener('change', onMq)

    let width = 0
    let height = 0
    let dpr = 1
    let raf = 0
    let visible = true
    let lastT = performance.now()

    const pointer = { x: 0.5, y: 0.45, active: false, lastMoveAt: 0 }
    let wasCursorMode = false
    const predator = {
      x: 0,
      y: 0,
      vx: 0,
      vy: 0,
      angle: 0,
      speed: 0,
      ready: false,
      mode: 'patrol' as PredatorMode,
      /** Countdown in current mode (strike / stalk / herd / brief patrol) */
      modeIn: 0,
      targetIndex: -1,
      biteCooldown: 0,
      /** Patrol / stalk / herd aim point */
      wx: 0,
      wy: 0,
      patrolIn: 0,
      /** Orbit side while stalking / herding a pack */
      flank: 1 as -1 | 1,
      /** Committed pack raid — keep cutting the same school */
      focusX: 0,
      focusY: 0,
      hasFocus: false,
      packKills: 0,
      packRaidGoal: 3,
      /** Mouth open vs closed */
      mouthOpen: false,
      /** Hold open mouth after a catch */
      openHold: 0,
      /**
       * Depth facing while turning only: -1 toward camera (…4), 0 profile, +1 away (…8).
       * Stays at 0 (default swim/open) unless changing direction.
       */
      pitch: 0,
      prevAngle: 0,
      prevHeading: 0,
      /** Keep a bank pose visible briefly after a turn impulse */
      bankHold: 0,
      bankSign: 1,
      bankIntensity: 0,
      /** Opposite bank waiting until pitch eases through profile */
      bankFlipPending: 0 as -1 | 0 | 1,
      bankPendingIntensity: 0,
      /** Compound size growth — ×1.01 per fish eaten */
      grow: 1,
    }
    const particles: Particle[] = []

    const isMobile = () => window.matchMedia('(max-width: 768px)').matches
    const schoolCount = () => (isMobile() ? 20 : 50)
    const predatorBaseSize = () => (isMobile() ? 59 : PREDATOR_SIZE)
    const predatorSize = () => Math.min(PREDATOR_SIZE_MAX, predatorBaseSize() * predator.grow)
    /** Speed grows with size, but only half as fast as the size increase */
    const predatorSpeedScale = () => {
      const sizeRatio = predatorSize() / predatorBaseSize()
      return 1 + (sizeRatio - 1) * 0.5
    }
    /** Cruise calm; chase paced just above prey flee (~105–125) without screen zooms */
    const predatorCruisePace = () => (isMobile() ? 0.65 : 0.82)
    const predatorChasePace = () => (isMobile() ? 0.9 : 1)

    let fish: Fish[] = []

    type TintedSprite = { canvas: HTMLCanvasElement; ready: boolean }
    const loadTintedSet = (srcs: readonly string[]) =>
      srcs.map((src) => {
        const canvas = document.createElement('canvas')
        canvas.width = SPRITE_TINT_SIZE
        canvas.height = SPRITE_TINT_SIZE
        const entry: TintedSprite = { canvas, ready: false }
        const img = new Image()
        img.onload = () => {
          const tctx = canvas.getContext('2d')
          if (!tctx) return
          tctx.clearRect(0, 0, SPRITE_TINT_SIZE, SPRITE_TINT_SIZE)
          tctx.drawImage(img, 0, 0, SPRITE_TINT_SIZE, SPRITE_TINT_SIZE)
          tctx.globalCompositeOperation = 'source-in'
          tctx.fillStyle = SPRITE_TINT
          tctx.fillRect(0, 0, SPRITE_TINT_SIZE, SPRITE_TINT_SIZE)
          tctx.globalCompositeOperation = 'source-over'
          entry.ready = true
        }
        img.src = src
        return entry
      })

    const swimSprites = loadTintedSet(SWIM_SPRITES)
    const openSprites = loadTintedSet(OPEN_SPRITES)

    /** Map turn pitch → 0 profile, 1/5 light bank, rarely 2/6 — never 3–4/7–8 */
    const pitchToFrame = (pitch: number) => {
      const abs = Math.abs(pitch)
      if (abs < 0.06) return 0
      const step = abs < 0.58 ? 1 : 2
      return pitch < 0 ? step : 4 + step
    }

    const currentSprite = (): HTMLCanvasElement | null => {
      const frame = pitchToFrame(predator.pitch)
      const set = predator.mouthOpen ? openSprites : swimSprites
      const entry = set[frame] ?? set[0]
      if (entry?.ready) return entry.canvas
      // Fallback to profile of either set
      if (set[0]?.ready) return set[0].canvas
      if (swimSprites[0]?.ready) return swimSprites[0].canvas
      return null
    }

    const updateMouth = (nearestPreyDist: number, engaged: boolean) => {
      if (predator.openHold > 0) {
        predator.mouthOpen = true
        return
      }
      predator.mouthOpen = engaged && nearestPreyDist < OPEN_MOUTH_DIST
    }

    const livingIndices = () => {
      const ids: number[] = []
      for (let i = 0; i < fish.length; i++) {
        if (fish[i].life === 1) ids.push(i)
      }
      return ids
    }

    const clampAim = (x: number, y: number) => {
      const pad = 48
      return {
        x: Math.min(width - pad, Math.max(pad, x)),
        y: Math.min(height - pad, Math.max(pad, y)),
      }
    }

    const setMode = (mode: PredatorMode, duration: number) => {
      predator.mode = mode
      predator.modeIn = duration
      if (mode !== 'strike') predator.targetIndex = -1
    }

    const clearPackFocus = () => {
      predator.hasFocus = false
      predator.packKills = 0
    }

    const commitToPack = (pack: PackInfo, freshRaid = false) => {
      predator.focusX = pack.cx
      predator.focusY = pack.cy
      predator.hasFocus = true
      if (freshRaid) {
        predator.packKills = 0
        // Aim to cut several from the same school before moving on
        predator.packRaidGoal = Math.min(5, Math.max(2, Math.floor(pack.count * 0.55) + (Math.random() < 0.5 ? 1 : 0)))
      }
    }

    const pickPatrolPoint = () => {
      const anchor = marginSchoolAnchor(width, height)
      const aim = clampAim(
        anchor.x + (Math.random() - 0.5) * 80,
        anchor.y + (Math.random() - 0.5) * 60,
      )
      predator.wx = aim.x
      predator.wy = aim.y
      predator.patrolIn = 0.6 + Math.random() * 0.7
    }

    const packFromSeed = (seedIndex: number, alive: number[]): PackInfo => {
      const seed = fish[seedIndex]
      const members: number[] = []
      let cx = 0
      let cy = 0
      let vx = 0
      let vy = 0
      for (const j of alive) {
        if (Math.hypot(fish[j].x - seed.x, fish[j].y - seed.y) < PACK_RADIUS) {
          members.push(j)
          cx += fish[j].x
          cy += fish[j].y
          vx += fish[j].vx
          vy += fish[j].vy
        }
      }
      const n = members.length || 1
      return { cx: cx / n, cy: cy / n, vx: vx / n, vy: vy / n, count: n, members }
    }

    /** Densest local group — packs beat lone fish for stalk interest */
    const findBestPack = (): PackInfo | null => {
      const alive = livingIndices()
      if (alive.length === 0) return null

      let bestSeed = alive[0]
      let bestScore = -Infinity
      for (const i of alive) {
        const a = fish[i]
        let n = 0
        for (const j of alive) {
          if (Math.hypot(fish[j].x - a.x, fish[j].y - a.y) < PACK_RADIUS) n++
        }
        const dist = Math.hypot(a.x - predator.x, a.y - predator.y)
        const score = n * 1000 - dist * 0.15
        if (score > bestScore) {
          bestScore = score
          bestSeed = i
        }
      }

      return packFromSeed(bestSeed, alive)
    }

    /** Keep hunting the committed school as it moves */
    const resolveFocusPack = (): PackInfo | null => {
      if (!predator.hasFocus) return null
      const alive = livingIndices()
      if (alive.length === 0) {
        clearPackFocus()
        return null
      }

      let bestSeed = -1
      let bestScore = -Infinity
      for (const i of alive) {
        const a = fish[i]
        const toFocus = Math.hypot(a.x - predator.focusX, a.y - predator.focusY)
        if (toFocus > PACK_FOCUS_RADIUS * 1.35) continue
        let n = 0
        for (const j of alive) {
          if (Math.hypot(fish[j].x - a.x, fish[j].y - a.y) < PACK_RADIUS) n++
        }
        const score = n * 1000 - toFocus * 0.4
        if (score > bestScore) {
          bestScore = score
          bestSeed = i
        }
      }

      if (bestSeed < 0) {
        // Focus drifted — try nearest survivors to last focus
        let nearest = -1
        let nearestD = Infinity
        for (const i of alive) {
          const d = Math.hypot(fish[i].x - predator.focusX, fish[i].y - predator.focusY)
          if (d < nearestD) {
            nearestD = d
            nearest = i
          }
        }
        if (nearest < 0 || nearestD > PACK_FOCUS_RADIUS * 1.8) {
          clearPackFocus()
          return null
        }
        bestSeed = nearest
      }

      const pack = packFromSeed(bestSeed, alive)
      predator.focusX = pack.cx
      predator.focusY = pack.cy
      return pack
    }

    /** Stalk / herd aim: flank the pack, slightly behind its travel */
    const packFlankPoint = (pack: PackInfo, dist: number) => {
      const spd = Math.hypot(pack.vx, pack.vy) || 1
      const fx = pack.vx / spd
      const fy = pack.vy / spd
      // Behind the pack's heading + lateral flank
      const bx = pack.cx - fx * dist * 0.55
      const by = pack.cy - fy * dist * 0.55
      const lx = -fy * predator.flank
      const ly = fx * predator.flank
      return clampAim(bx + lx * dist * 0.75, by + ly * dist * 0.75)
    }

    /** Prefer edge / straggler in the committed pack — stay in that school */
    const pickStrikeTarget = (pack: PackInfo | null, excludeIndex = -1) => {
      const alive = livingIndices()
      if (alive.length === 0) {
        predator.targetIndex = -1
        return
      }

      let pool = pack && pack.members.length > 0 ? pack.members.filter((i) => i !== excludeIndex) : []
      if (pool.length === 0) {
        pool = alive.filter((i) => i !== excludeIndex)
      }
      if (pool.length === 0) {
        predator.targetIndex = -1
        return
      }

      let best = pool[0]
      let bestScore = -Infinity

      for (const i of pool) {
        const f = fish[i]
        const d = Math.hypot(f.x - predator.x, f.y - predator.y)
        const bearing = Math.atan2(f.y - predator.y, f.x - predator.x)
        let ang = bearing - predator.angle
        while (ang > Math.PI) ang -= Math.PI * 2
        while (ang < -Math.PI) ang += Math.PI * 2

        let neighbours = 0
        for (const j of pool) {
          if (j !== i && Math.hypot(fish[j].x - f.x, fish[j].y - f.y) < PACK_RADIUS * 0.7) {
            neighbours++
          }
        }

        const ahead = 1 - Math.min(1, Math.abs(ang) / Math.PI)
        const edge = 1 / (1 + neighbours)
        // During a raid, strongly prefer the next-closest packmate
        const prox = 1 / (1 + d * 0.0025)
        const score = prox * 1.6 + edge * 1.1 + ahead * 0.7 + Math.random() * 0.2
        if (score > bestScore) {
          bestScore = score
          best = i
        }
      }

      predator.targetIndex = best
    }

    const enterPatrol = (duration = 0.35 + Math.random() * 0.4) => {
      clearPackFocus()
      setMode('patrol', duration)
      pickPatrolPoint()
    }

    /** Continue the current pack raid — chain strikes on remaining members */
    const continuePackRaid = () => {
      const pack = resolveFocusPack()
      if (!pack || pack.count < 1) {
        clearPackFocus()
        engagePack(findBestPack())
        return
      }
      // Still fish left and raid not finished → keep striking this school
      if (predator.packKills < predator.packRaidGoal && pack.count >= 1) {
        enterStrike(pack, false)
        return
      }
      // Raid done — only then look for a new school
      clearPackFocus()
      engagePack(findBestPack())
    }

    /** Prefer stalk/strike over idle patrol whenever prey exists */
    const engagePack = (pack: PackInfo | null) => {
      if (!pack || pack.count < 1) {
        enterPatrol()
        return
      }
      commitToPack(pack, true)
      if (pack.count >= PACK_MIN) {
        if (Math.random() < 0.3) enterHerd(pack)
        else if (Math.random() < 0.45) enterStalk(pack)
        else enterStrike(pack, false)
      } else {
        enterStrike(pack, false)
      }
    }

    const enterStalk = (pack: PackInfo) => {
      commitToPack(pack)
      predator.flank = Math.random() < 0.5 ? -1 : 1
      const dist = STALK_DIST_MIN + Math.random() * (STALK_DIST_MAX - STALK_DIST_MIN)
      const aim = packFlankPoint(pack, dist)
      predator.wx = aim.x
      predator.wy = aim.y
      setMode('stalk', 0.7 + Math.random() * 0.9)
    }

    const enterHerd = (pack: PackInfo) => {
      commitToPack(pack)
      predator.flank = Math.random() < 0.45 ? ((-predator.flank) as -1 | 1) : predator.flank
      const dist = 120 + Math.random() * 70
      const aim = packFlankPoint(pack, dist)
      predator.wx = aim.x
      predator.wy = aim.y
      setMode('herd', 0.55 + Math.random() * 0.7)
    }

    const enterStrike = (pack: PackInfo | null, freshRaid = false) => {
      if (pack) commitToPack(pack, freshRaid)
      const focus = pack ?? resolveFocusPack()
      pickStrikeTarget(focus)
      if (predator.targetIndex < 0) {
        clearPackFocus()
        enterPatrol()
        return
      }
      setMode('strike', STRIKE_MAX * (0.75 + Math.random() * 0.35))
    }

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2)
      const nextW = canvas.clientWidth
      const nextH = canvas.clientHeight
      if (nextW < 2 || nextH < 2) return
      const bufW = Math.floor(nextW * dpr)
      const bufH = Math.floor(nextH * dpr)
      if (nextW === width && nextH === height && canvas.width === bufW && canvas.height === bufH) {
        return
      }
      width = nextW
      height = nextH
      canvas.width = bufW
      canvas.height = bufH
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

      const target = schoolCount()
      if (fish.length === 0) {
        fish = createSchool(width, height, target)
      } else if (fish.length < target) {
        while (fish.length < target) fish.push(createFish(width, height, true))
      } else if (fish.length > target) {
        fish.length = target
      }

      if (showPredator && !predator.ready) {
        const start = marginSchoolAnchor(width, height)
        predator.x = start.x
        predator.y = start.y
        predator.angle = start.heading
        predator.prevAngle = predator.angle
        predator.prevHeading = predator.angle
        predator.speed = 110 * predatorCruisePace() * predatorSpeedScale()
        predator.ready = true
        engagePack(findBestPack())
      }
    }

    const heroEl = () => (wrap.closest('section') as HTMLElement | null) ?? wrap

    const setPointerFromEvent = (e: PointerEvent) => {
      if (!finePointer || reduced) return
      const rect = heroEl().getBoundingClientRect()
      const inside =
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom
      pointer.x = e.clientX - rect.left
      pointer.y = e.clientY - rect.top
      if (inside) {
        // Any move over the hero renews follow mode
        pointer.lastMoveAt = performance.now()
      }
      if (pointer.active && !inside) {
        // Leaving hero — resume autonomous hunt aggressively
        engagePack(findBestPack())
      }
      pointer.active = inside
    }

    const clearPointer = () => {
      if (pointer.active) {
        engagePack(findBestPack())
      }
      pointer.active = false
      pointer.lastMoveAt = 0
    }

    const emitFlecks = (x: number, y: number) => {
      const n = 3 + Math.floor(Math.random() * 3)
      for (let i = 0; i < n; i++) {
        const a = Math.random() * Math.PI * 2
        const s = 0.6 + Math.random() * 1.4
        particles.push({
          x,
          y,
          vx: Math.cos(a) * s,
          vy: Math.sin(a) * s,
          life: 1,
          size: 1 + Math.random() * 1.5,
        })
      }
    }

    const tryEat = (f: Fish, hunt: boolean) => {
      if (f.life !== 1 || predator.biteCooldown > 0) return
      // Measure from the mouth so growth doesn't invent a huge center hitbox
      const size = predatorSize()
      const mouthR = size * MOUTH_FORWARD_FRAC
      const mx = predator.x + Math.cos(predator.angle) * mouthR
      const my = predator.y + Math.sin(predator.angle) * mouthR
      const dist = Math.hypot(f.x - mx, f.y - my)
      const radius = size * (hunt ? BITE_SIZE_FRAC_HUNT : BITE_SIZE_FRAC)
      if (dist < radius) {
        f.life = 0.99
        emitFlecks(f.x, f.y)
        const next = predator.grow * GROW_PER_EAT
        if (predatorBaseSize() * next <= PREDATOR_SIZE_MAX) {
          predator.grow = next
        } else {
          predator.grow = PREDATOR_SIZE_MAX / predatorBaseSize()
        }
        predator.biteCooldown = 0.1
        const eatenIndex = fish.indexOf(f)
        predator.targetIndex = -1
        predator.mouthOpen = true
        predator.openHold = OPEN_HOLD
        if (hunt) {
          if (predator.hasFocus) predator.packKills += 1
          // Chain the next packmate immediately (0.1s bite gate only)
          const pack = resolveFocusPack()
          if (pack && pack.count >= 1 && predator.packKills < predator.packRaidGoal) {
            pickStrikeTarget(pack, eatenIndex)
            if (predator.targetIndex >= 0) {
              setMode('strike', STRIKE_MAX * (0.7 + Math.random() * 0.35))
            } else {
              continuePackRaid()
            }
          } else {
            continuePackRaid()
          }
        }
      }
    }

    const rotateToward = (dx: number, dy: number, turn = 0.08) => {
      if (Math.hypot(dx, dy) < 0.12) return
      const targetAngle = Math.atan2(dy, dx)
      let diff = targetAngle - predator.angle
      while (diff > Math.PI) diff -= Math.PI * 2
      while (diff < -Math.PI) diff += Math.PI * 2
      predator.angle += diff * turn
    }

    const draw = (now: number) => {
      if (!visible) {
        raf = 0
        return
      }

      const dt = Math.min(0.033, (now - lastT) / 1000)
      lastT = now

      ctx.clearRect(0, 0, width, height)

      // Follow while the cursor is over the hero and recently moved; idle → auto hunt
      const cursorIdle = pointer.lastMoveAt <= 0 || now - pointer.lastMoveAt > CURSOR_IDLE_MS
      const cursorMode = finePointer && pointer.active && !reduced && !cursorIdle
      const huntMode = !reduced && !cursorMode

      if (wasCursorMode && huntMode && predator.ready) {
        // Cursor went idle (or left) — kick autonomous hunt
        engagePack(findBestPack())
      }
      wasCursorMode = cursorMode

      // Predator motion
      if (!reduced && predator.ready) {
        predator.biteCooldown = Math.max(0, predator.biteCooldown - dt)
        if (predator.openHold > 0) {
          predator.openHold = Math.max(0, predator.openHold - dt)
        }

        if (cursorMode) {
          const tx = pointer.x
          const ty = pointer.y
          const spd = predatorSpeedScale()
          const ease = (isMobile() ? 0.04 : 0.05) * spd
          const prevX = predator.x
          const prevY = predator.y
          predator.x += (tx - predator.x) * ease
          predator.y += (ty - predator.y) * ease
          predator.vx = (predator.x - prevX) / Math.max(dt, 0.001)
          predator.vy = (predator.y - prevY) / Math.max(dt, 0.001)
          rotateToward(predator.x - prevX, predator.y - prevY, Math.min(0.2, 0.08 * spd))
        } else if (huntMode) {
          predator.modeIn -= dt
          predator.patrolIn -= dt

          const cruisePace = predatorCruisePace()
          const chasePace = predatorChasePace()
          const spd = predatorSpeedScale()
          // Prefer the committed school; only scout globally when unfocused
          const pack = predator.hasFocus ? resolveFocusPack() : findBestPack()

          // --- Mode transitions (aggressive: hunt > scout) ---
          if (predator.mode === 'patrol') {
            if (pack && pack.count >= 1) {
              engagePack(pack)
            } else if (predator.modeIn <= 0) {
              predator.modeIn = 0.35 + Math.random() * 0.35
              pickPatrolPoint()
            }
          } else if (predator.mode === 'stalk') {
            if (!pack || pack.count < 1) {
              continuePackRaid()
            } else {
              const dist = STALK_DIST_MIN + (STALK_DIST_MAX - STALK_DIST_MIN) * 0.45
              const aim = packFlankPoint(pack, dist)
              predator.wx = aim.x
              predator.wy = aim.y
              const toPack = Math.hypot(pack.cx - predator.x, pack.cy - predator.y)
              // Short stalk — commit quickly into the same pack
              if (predator.modeIn <= 0 || toPack < STALK_DIST_MAX * 0.9) {
                if (pack.count >= PACK_MIN && Math.random() < 0.3) enterHerd(pack)
                else enterStrike(pack, false)
              }
            }
          } else if (predator.mode === 'herd') {
            if (!pack || pack.count < 1) {
              continuePackRaid()
            } else {
              const dist = 110 + Math.sin(now * 0.002) * 30
              const aim = packFlankPoint(pack, dist)
              predator.wx = aim.x
              predator.wy = aim.y
              if (predator.modeIn <= 0) {
                enterStrike(pack, false)
              }
            }
          } else if (predator.mode === 'strike') {
            const prey = predator.targetIndex >= 0 ? fish[predator.targetIndex] : null
            if (!prey || prey.life !== 1) {
              // Lost that fish — cut the next packmate, don't abandon the school
              continuePackRaid()
            } else if (predator.modeIn <= 0) {
              // Long chase abort — switch to another fish in the same pack
              if (pack && pack.count >= 2) {
                pickStrikeTarget(pack, predator.targetIndex)
                if (predator.targetIndex >= 0) {
                  setMode('strike', STRIKE_MAX * (0.7 + Math.random() * 0.3))
                } else {
                  continuePackRaid()
                }
              } else {
                continuePackRaid()
              }
            }
          }

          // --- Steering by mode ---
          const prey =
            predator.mode === 'strike' && predator.targetIndex >= 0
              ? fish[predator.targetIndex]
              : null
          let aimX = predator.wx
          let aimY = predator.wy
          let cruise = 110 * cruisePace * spd
          let turn = 2.2
          let striking = false
          let chaseDist = Infinity

          if (predator.mode === 'strike' && prey && prey.life === 1) {
            striking = true
            chaseDist = Math.hypot(prey.x - predator.x, prey.y - predator.y)
            const lead = chaseDist > 160 ? 0.38 : 0.22
            aimX = prey.x + prey.vx * lead
            aimY = prey.y + prey.vy * lead
            if (chaseDist > 220) {
              cruise = 235 * chasePace * spd
              turn = 4.6
            } else if (chaseDist > 100) {
              cruise = 285 * chasePace * spd
              turn = 6.5
            } else {
              cruise = 330 * chasePace * spd
              turn = 9
            }
          } else if (predator.mode === 'herd') {
            if (predator.patrolIn <= 0 || Math.hypot(predator.wx - predator.x, predator.wy - predator.y) < 40) {
              predator.patrolIn = 0.35
              if (pack) {
                const aim = packFlankPoint(pack, 120 + Math.random() * 50)
                predator.wx = aim.x
                predator.wy = aim.y
              }
            }
            cruise = (155 + Math.sin(now * 0.002) * 20) * cruisePace * spd
            turn = 3.4
          } else if (predator.mode === 'stalk') {
            cruise = (95 + Math.sin(now * 0.0011) * 12) * cruisePace * spd
            turn = 2.4
          } else {
            // brief patrol only when nothing to hunt
            if (predator.patrolIn <= 0 || Math.hypot(predator.wx - predator.x, predator.wy - predator.y) < 36) {
              pickPatrolPoint()
            }
            if (predator.mode === 'patrol' && pack && pack.count >= 1) {
              const scout = clampAim(pack.cx, pack.cy)
              predator.wx = scout.x
              predator.wy = scout.y
            }
            cruise = (120 + Math.sin(now * 0.0013) * 14) * cruisePace * spd
            turn = 2.4
          }

          // Swim model: steer heading, then move forward along it
          const desired = Math.atan2(aimY - predator.y, aimX - predator.x)
          let diff = desired - predator.angle
          while (diff > Math.PI) diff -= Math.PI * 2
          while (diff < -Math.PI) diff += Math.PI * 2
          const maxTurn = turn * dt
          predator.angle += Math.max(-maxTurn, Math.min(maxTurn, diff))

          const align = 1 - Math.min(1, Math.abs(diff) / Math.PI)
          const targetSpd = striking
            ? cruise * (0.93 + align * 0.14)
            : cruise * (0.9 + align * 0.08)
          const accel = !striking ? (predator.mode === 'herd' ? 2.4 : 1.6) : chaseDist < 100 ? 8 : 5.5
          predator.speed += (targetSpd - predator.speed) * Math.min(1, accel * dt)
          const speedCap = (striking ? 345 * chasePace : predator.mode === 'herd' ? 175 * cruisePace : 135 * cruisePace) * spd
          if (predator.speed > speedCap) predator.speed = speedCap

          // Weave while scouting / stalking — strikes stay committed on-line
          const weave =
            striking ? 0 : Math.sin(now * 0.003 + predator.x * 0.008) * (predator.mode === 'stalk' ? 0.28 : 0.18)
          const moveAngle = predator.angle + weave * dt * 3
          predator.vx = Math.cos(moveAngle) * predator.speed
          predator.vy = Math.sin(moveAngle) * predator.speed
          predator.x += predator.vx * dt
          predator.y += predator.vy * dt

          // Allow 10% outside the hero; only gently turn back beyond that
          const minX = -width * PREDATOR_OVERFLOW
          const maxX = width * (1 + PREDATOR_OVERFLOW)
          const minY = -height * PREDATOR_OVERFLOW
          const maxY = height * (1 + PREDATOR_OVERFLOW)
          if (predator.x < minX) {
            predator.x = minX
            predator.angle = Math.abs(predator.angle) < Math.PI / 2 ? predator.angle : Math.PI - predator.angle
          } else if (predator.x > maxX) {
            predator.x = maxX
            predator.angle = Math.abs(predator.angle) < Math.PI / 2 ? Math.PI - predator.angle : predator.angle
          }
          if (predator.y < minY) {
            predator.y = minY
            predator.angle = -predator.angle
          } else if (predator.y > maxY) {
            predator.y = maxY
            predator.angle = -predator.angle
          }
        }

        // Depth frames on direction change (angle or move heading) — brief, subtle hold
        let angDelta = predator.angle - predator.prevAngle
        while (angDelta > Math.PI) angDelta -= Math.PI * 2
        while (angDelta < -Math.PI) angDelta += Math.PI * 2
        predator.prevAngle = predator.angle

        const moveHeading = Math.atan2(predator.vy, predator.vx || 0.001)
        let headDelta = moveHeading - predator.prevHeading
        while (headDelta > Math.PI) headDelta -= Math.PI * 2
        while (headDelta < -Math.PI) headDelta += Math.PI * 2
        predator.prevHeading = moveHeading

        const invDt = 1 / Math.max(dt, 0.001)
        const turnRate = Math.abs(angDelta) > Math.abs(headDelta) ? angDelta * invDt : headDelta * invDt
        const TURN_START = 0.14
        const TURN_FULL = 2.0
        const absTurn = Math.abs(turnRate)

        if (absTurn > TURN_START && predator.bankFlipPending === 0) {
          const t = Math.min(1, (absTurn - TURN_START) / (TURN_FULL - TURN_START))
          const sign = (turnRate > 0 ? -1 : 1) as -1 | 1
          // Clear light bank (1/5); very rarely a touch deeper (2/6)
          const depth = t > 0.9 && Math.random() < 0.06 ? 0.72 : 0.45
          const banking = predator.bankHold > 0 || Math.abs(predator.pitch) > 0.1

          if (!banking) {
            predator.bankSign = sign
            predator.bankIntensity = depth
            predator.bankHold = 0.42
          } else if (sign === predator.bankSign) {
            // Stay banked for the whole turn arc
            predator.bankHold = Math.max(predator.bankHold, 0.32)
          } else if (absTurn > TURN_START * 2 && Math.abs(predator.pitch) > 0.25) {
            // Reverse only on a clear opposite turn — ease through profile first
            predator.bankFlipPending = sign
            predator.bankPendingIntensity = depth
            predator.bankHold = 0
            predator.bankIntensity = 0
          }
        }

        if (predator.bankFlipPending !== 0 && Math.abs(predator.pitch) < 0.07) {
          predator.bankSign = predator.bankFlipPending
          predator.bankIntensity = predator.bankPendingIntensity || 0.45
          predator.bankHold = 0.42
          predator.bankFlipPending = 0
          predator.bankPendingIntensity = 0
        }

        let targetPitch = 0
        if (predator.bankHold > 0) {
          predator.bankHold = Math.max(0, predator.bankHold - dt)
          targetPitch = predator.bankSign * predator.bankIntensity
          if (predator.bankHold === 0) predator.bankIntensity = 0
        }
        // Quick into the bank so the angle reads; slower return to profile
        const ease = targetPitch === 0 ? 5.5 : 16
        predator.pitch += (targetPitch - predator.pitch) * Math.min(1, ease * dt)
        if (Math.abs(predator.pitch) < 0.03) predator.pitch = 0
      }

      // School — boids + soft copy avoidance
      const zoneCx = width * 0.5
      const zoneCy = height * TEXT_ZONE_CY
      const zoneRx = Math.max(80, width * TEXT_ZONE_RX)
      const zoneRy = Math.max(70, height * TEXT_ZONE_RY)

      const living: Fish[] = []
      for (const f of fish) {
        if (f.life === 1) living.push(f)
      }

      const accelX = new Float32Array(living.length)
      const accelY = new Float32Array(living.length)

      if (!reduced && living.length > 0) {
        for (let i = 0; i < living.length; i++) {
          const a = living[i]
          let sepX = 0
          let sepY = 0
          let sepN = 0
          let aliX = 0
          let aliY = 0
          let aliN = 0
          let cohX = 0
          let cohY = 0
          let cohN = 0

          for (let j = 0; j < living.length; j++) {
            if (i === j) continue
            const b = living[j]
            const dx = a.x - b.x
            const dy = a.y - b.y
            const d = Math.hypot(dx, dy) || 0.0001

            if (d < SEPARATION_RADIUS) {
              const strength = (SEPARATION_RADIUS - d) / SEPARATION_RADIUS
              sepX += (dx / d) * strength
              sepY += (dy / d) * strength
              sepN++
            }
            if (d < ALIGN_RADIUS) {
              aliX += b.vx
              aliY += b.vy
              aliN++
            }
            if (d < COHESION_RADIUS) {
              cohX += b.x
              cohY += b.y
              cohN++
            }
          }

          let ax = 0
          let ay = 0

          // Fear collapses flocking and amps separation — school explodes apart
          let fear = 0
          if (predator.ready) {
            const pdist = Math.hypot(a.x - predator.x, a.y - predator.y)
            if (pdist < FLEE_RADIUS) fear = 1 - pdist / FLEE_RADIUS
          }
          const flockScale = Math.max(0, 1 - fear * 1.35)
          const sepScale = 1 + fear * 2.4

          if (sepN > 0) {
            ax += (sepX / sepN) * W_SEPARATION * 220 * sepScale
            ay += (sepY / sepN) * W_SEPARATION * 220 * sepScale
          }

          if (aliN > 0) {
            const mx = aliX / aliN
            const my = aliY / aliN
            ax += (mx - a.vx) * W_ALIGNMENT * flockScale * 2.4
            ay += (my - a.vy) * W_ALIGNMENT * flockScale * 2.4
          }
          if (cohN > 0) {
            const cx = cohX / cohN
            const cy = cohY / cohN
            // When afraid, invert cohesion so neighbors push apart
            const cohSign = fear > 0.35 ? -0.85 : 1
            ax += (cx - a.x) * W_COHESION * flockScale * 1.1 * cohSign
            ay += (cy - a.y) * W_COHESION * flockScale * 1.1 * cohSign
          }

          ax += Math.sin(a.phase) * W_WANDER * 40 * (1 - fear * 0.7)
          ay += Math.cos(a.phase * 0.87) * W_WANDER * 40 * (1 - fear * 0.7)

          if (predator.ready && fear > 0) {
            const pdx = a.x - predator.x
            const pdy = a.y - predator.y
            const pdist = Math.hypot(pdx, pdy) || 1
            const fx = pdx / pdist
            const fy = pdy / pdist
            const targeted =
              huntMode &&
              predator.mode === 'strike' &&
              predator.targetIndex >= 0 &&
              fish[predator.targetIndex] === a

            // Timed jukes — zig-zag / cut rather than straight flee
            a.jukeIn -= dt
            if (a.jukeIn <= 0) {
              a.jukeDir *= -1
              // Targeted fish juke more often and harder
              a.jukeIn = targeted
                ? 0.12 + Math.random() * 0.22
                : 0.28 + Math.random() * 0.45
              // Occasional sharp break (almost perpendicular cut)
              if (Math.random() < (targeted ? 0.45 : 0.22)) {
                const cut = a.jukeDir * (220 + fear * 280)
                ax += -fy * cut
                ay += fx * cut
              }
            }

            const zig = Math.sin(a.phase * 3.2 + now * 0.012) * (0.55 + fear * 0.9)
            const lateral = a.jukeDir * (0.55 + fear * 0.95 + (targeted ? 0.45 : 0)) + zig
            let fleeBoost = W_FLEE * (200 + fear * 340)
            if (targeted) fleeBoost *= 1.45
            ax += (fx * 0.75 + -fy * lateral) * fleeBoost
            ay += (fy * 0.75 + fx * lateral) * fleeBoost

            // S-curve weave while escaping
            const weaveAmp = (targeted ? 160 : 90) * fear
            ax += -fy * Math.sin(now * 0.014 + a.phase) * weaveAmp
            ay += fx * Math.sin(now * 0.014 + a.phase) * weaveAmp
          }

          // Soft text-zone bias — slide around the copy, not a hard wall
          if (avoidCopy) {
            const nx = (a.x - zoneCx) / zoneRx
            const ny = (a.y - zoneCy) / zoneRy
            const inside = nx * nx + ny * ny
            if (inside < 1.15) {
              const push = Math.max(0, 1.15 - inside)
              const dist = Math.hypot(nx, ny) || 0.0001
              const side = a.x < zoneCx ? -1 : 1
              ax += (nx / dist) * push * W_TEXT_ZONE * 160 + side * push * W_TEXT_ZONE * 50
              ay += (ny / dist) * push * W_TEXT_ZONE * 120
            }
          }

          accelX[i] = ax
          accelY[i] = ay
        }

        for (let i = 0; i < living.length; i++) {
          const f = living[i]
          const prevAngle = Math.atan2(f.vy, f.vx || 0.001)
          f.phase += dt * (2 + Math.hypot(f.vx, f.vy) * 0.02)
          f.vx += accelX[i] * dt
          f.vy += accelY[i] * dt

          const pdistNow = predator.ready ? Math.hypot(f.x - predator.x, f.y - predator.y) : Infinity
          const fleeing = pdistNow < FLEE_RADIUS
          const fear = fleeing ? 1 - pdistNow / FLEE_RADIUS : 0
          const cruiseTarget = PREY_CRUISE * (0.9 + Math.sin(f.phase * 0.4) * 0.12)
          const targetSpd = fleeing ? PREY_FLEE_SPEED * (0.8 + fear * 0.35) : cruiseTarget
          const maxSpd = fleeing ? PREY_MAX_SPEED : PREY_CRUISE * 1.25

          const spd = Math.hypot(f.vx, f.vy) || 0.001
          const desired = Math.min(maxSpd, targetSpd)
          // Faster speed-up when scattering so the burst reads
          const accelRate = fleeing ? 6.5 : 3.2
          const nextSpd = spd + (desired - spd) * Math.min(1, accelRate * dt)
          f.vx = (f.vx / spd) * nextSpd
          f.vy = (f.vy / spd) * nextSpd

          // Fleeing fish can cut harder for elaborate evasion
          const newAngle = Math.atan2(f.vy, f.vx)
          let turn = newAngle - prevAngle
          while (turn > Math.PI) turn -= Math.PI * 2
          while (turn < -Math.PI) turn += Math.PI * 2
          const targeted =
            huntMode &&
            predator.mode === 'strike' &&
            predator.targetIndex >= 0 &&
            fish[predator.targetIndex] === f
          const maxTurn = (fleeing ? (targeted ? 12 : 9.5) : 3.2) * dt
          if (Math.abs(turn) > maxTurn) {
            const clamped = prevAngle + Math.sign(turn) * maxTurn
            const s = Math.hypot(f.vx, f.vy)
            f.vx = Math.cos(clamped) * s
            f.vy = Math.sin(clamped) * s
          }

          f.x += f.vx * dt
          f.y += f.vy * dt

          const m = 28
          if (f.x < -m) f.x = width + m
          if (f.x > width + m) f.x = -m
          if (f.y < -m) f.y = height + m
          if (f.y > height + m) f.y = -m
        }
      }

      for (const f of fish) {
        if (f.life < 0) {
          f.respawnIn -= dt
          if (f.respawnIn <= 0) {
            const spawn = spawnAtEdge(width, height)
            f.x = spawn.x
            f.y = spawn.y
            f.vx = spawn.vx
            f.vy = spawn.vy
            f.life = 1
            f.size = 5 + Math.random() * 5
            f.alpha = 0.38 + Math.random() * 0.22
            f.jukeDir = Math.random() < 0.5 ? -1 : 1
            f.jukeIn = 0.2 + Math.random() * 0.5
          }
          continue
        }

        if (f.life < 1) {
          f.life -= dt / EAT_DURATION
          if (f.life <= 0) {
            f.life = -1
            f.respawnIn = 0.4 + Math.random() * 0.8
            continue
          }
          const scale = Math.max(0.05, f.life)
          const angle = Math.atan2(f.vy, f.vx)
          drawLittleFish(ctx, f.x, f.y, angle, f.size, f.alpha * f.life, scale, preyColor)
          continue
        }

        if (!reduced && predator.ready) {
          if (cursorMode || huntMode) tryEat(f, huntMode)
        }

        const angle = Math.atan2(f.vy, f.vx || 0.001)
        let zoneFade = 1
        if (avoidCopy) {
          const nx = (f.x - zoneCx) / zoneRx
          const ny = (f.y - zoneCy) / zoneRy
          const inside = nx * nx + ny * ny
          zoneFade = inside < 1 ? 0.45 + inside * 0.55 : 1
        }
        drawLittleFish(ctx, f.x, f.y, angle, f.size, f.alpha * zoneFade, 1, preyColor)
      }

      // Flecks
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i]
        p.x += p.vx
        p.y += p.vy
        p.life -= dt * 3.2
        if (p.life <= 0) {
          particles.splice(i, 1)
          continue
        }
        ctx.globalAlpha = p.life * 0.55
        ctx.fillStyle = '#0f172a'
        ctx.beginPath()
        ctx.arc(p.x, p.y, p.size * p.life, 0, Math.PI * 2)
        ctx.fill()
        ctx.globalAlpha = 1
      }

      // Predator sprite — open/closed × depth frames (toward / profile / away)
      if (!reduced && predator.ready) {
        let nearest = Infinity
        for (const f of fish) {
          if (f.life !== 1) continue
          const d = Math.hypot(f.x - predator.x, f.y - predator.y)
          if (d < nearest) nearest = d
        }
        const striking =
          predator.mode === 'strike' &&
          predator.targetIndex >= 0 &&
          fish[predator.targetIndex]?.life === 1
        updateMouth(nearest, striking || (cursorMode && nearest < OPEN_MOUTH_DIST))

        const sprite = currentSprite()
        if (sprite) {
          const frame = pitchToFrame(predator.pitch)
          // Facing camera reads slightly larger; facing away slightly smaller
          const depthScale =
            frame === 0 ? 1 : frame <= 4 ? 1 + frame * 0.012 : 1 - (frame - 4) * 0.012
          const s = Math.min(PREDATOR_SIZE_MAX, predatorSize() * depthScale)
          ctx.save()
          ctx.translate(predator.x, predator.y)
          ctx.rotate(predator.angle)
          const facingLeft = Math.cos(predator.angle) < 0
          if (facingLeft) ctx.scale(1, -1)
          ctx.globalAlpha = cursorMode ? 0.95 : 0.9
          ctx.drawImage(sprite, -s * 0.5, -s * 0.5, s, s)
          ctx.restore()
        }
      }

      raf = requestAnimationFrame(draw)
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        const was = visible
        visible = entry.isIntersecting
        if (visible && !was && raf === 0) {
          lastT = performance.now()
          raf = requestAnimationFrame(draw)
        }
      },
      { threshold: 0.05 },
    )
    io.observe(wrap)

    resize()
    lastT = performance.now()
    raf = requestAnimationFrame(draw)
    window.addEventListener('resize', resize)
    const ro = new ResizeObserver(() => resize())
    ro.observe(wrap)
    if (showPredator) {
      window.addEventListener('pointermove', setPointerFromEvent, { passive: true })
      document.documentElement.addEventListener('pointerleave', clearPointer)
      window.addEventListener('blur', clearPointer)
    }

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', resize)
      ro.disconnect()
      if (showPredator) {
        window.removeEventListener('pointermove', setPointerFromEvent)
        document.documentElement.removeEventListener('pointerleave', clearPointer)
        window.removeEventListener('blur', clearPointer)
      }
      reducedMq.removeEventListener('change', onMq)
      fineMq.removeEventListener('change', onMq)
      io.disconnect()
    }
  }, [showPredator, avoidCopy, fixedToViewport, preyColor, reducedMotion])

  if (reducedMotion) return null

  return (
    <div
      ref={wrapRef}
      className={
        fixedToViewport
          ? 'fixed inset-0 z-0 overflow-hidden pointer-events-none'
          : 'absolute inset-0 z-0 overflow-hidden pointer-events-none'
      }
      aria-hidden
    >
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
    </div>
  )
}
